import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  TestTube,
  AlertCircle,
  X,
  Upload,
  CheckCircle,
  Calendar,
} from 'lucide-react'
import Papa from 'papaparse'
import api, { getAllCattle } from '../../services/api'
import PageHeader from '../../components/PageHeader'
import clsx from 'clsx'

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const getMilkQualityTests = (ranchId, params = {}) =>
  api.get('/dairy/quality-tests', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

const createMilkQualityTest = (data) =>
  api.post('/dairy/quality-tests', data).then(res => res.data)

const bulkCreateQualityTests = (data) =>
  api.post('/dairy/quality-tests/bulk', data).then(res => res.data)

const getDairyCattle = (ranchId) =>
  getAllCattle(ranchId, { status: 'active', category: 'cow' })

// ---------------------------------------------------------------------------
// Grade helpers
// ---------------------------------------------------------------------------

const gradeColors = {
  grade_a: 'bg-green-100 text-green-800',
  grade_b: 'bg-blue-100 text-blue-800',
  grade_c: 'bg-amber-100 text-amber-800',
  rejected: 'bg-red-100 text-red-800',
}

const gradeLabels = {
  grade_a: 'Grade A',
  grade_b: 'Grade B',
  grade_c: 'Grade C',
  rejected: 'Rejected',
}

// ---------------------------------------------------------------------------
// Add Test Modal
// ---------------------------------------------------------------------------

function AddTestModal({ ranchId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    ranch_id: ranchId,
    animal_id: '',
    test_date: new Date().toISOString().split('T')[0],
    fat_pct: '',
    protein_pct: '',
    lactose_pct: '',
    snf_pct: '',
    scc: '',
    bacteria_count: '',
    grade: 'grade_a',
    notes: '',
  })
  const [error, setError] = useState('')

  const { data: cattleData } = useQuery({
    queryKey: ['dairyCattle', ranchId],
    queryFn: () => getDairyCattle(ranchId),
  })

  const mutation = useMutation({
    mutationFn: createMilkQualityTest,
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.detail || 'Failed to create quality test'),
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    const payload = {
      ...formData,
      animal_id: formData.animal_id || null,
      fat_pct: formData.fat_pct ? parseFloat(formData.fat_pct) : null,
      protein_pct: formData.protein_pct ? parseFloat(formData.protein_pct) : null,
      lactose_pct: formData.lactose_pct ? parseFloat(formData.lactose_pct) : null,
      snf_pct: formData.snf_pct ? parseFloat(formData.snf_pct) : null,
      scc: formData.scc ? parseInt(formData.scc) : null,
      bacteria_count: formData.bacteria_count ? parseInt(formData.bacteria_count) : null,
    }
    mutation.mutate(payload)
  }

  const cows = cattleData || []

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Add Quality Test</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Animal selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source (leave blank for Bulk Tank)
            </label>
            <select
              value={formData.animal_id}
              onChange={(e) => setFormData({ ...formData, animal_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">Bulk Tank</option>
              {cows.map((cow) => (
                <option key={cow.id} value={cow.id}>
                  {cow.local_name || cow.rfid_tag} ({cow.tag_id || cow.rfid_tag})
                </option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Test Date</label>
            <input
              type="date"
              required
              value={formData.test_date}
              onChange={(e) => setFormData({ ...formData, test_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Composition row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fat %</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="15"
                value={formData.fat_pct}
                onChange={(e) => setFormData({ ...formData, fat_pct: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="e.g. 3.80"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Protein %</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="10"
                value={formData.protein_pct}
                onChange={(e) => setFormData({ ...formData, protein_pct: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="e.g. 3.20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lactose %</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="10"
                value={formData.lactose_pct}
                onChange={(e) => setFormData({ ...formData, lactose_pct: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="e.g. 4.70"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SNF %</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="15"
                value={formData.snf_pct}
                onChange={(e) => setFormData({ ...formData, snf_pct: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="e.g. 8.50"
              />
            </div>
          </div>

          {/* SCC & bacteria */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SCC (cells/mL)</label>
              <input
                type="number"
                min="0"
                value={formData.scc}
                onChange={(e) => setFormData({ ...formData, scc: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="e.g. 200000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bacteria Count</label>
              <input
                type="number"
                min="0"
                value={formData.bacteria_count}
                onChange={(e) => setFormData({ ...formData, bacteria_count: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="optional"
              />
            </div>
          </div>

          {/* Grade */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
            <select
              value={formData.grade}
              onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="grade_a">Grade A</option>
              <option value="grade_b">Grade B</option>
              <option value="grade_c">Grade C</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Optional observations..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              {mutation.isPending ? 'Saving...' : 'Save Test'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Bulk Import Modal
// ---------------------------------------------------------------------------

function BulkImportModal({ ranchId, onClose, onSuccess }) {
  const fileInputRef = useRef(null)
  const [parsedRows, setParsedRows] = useState([])
  const [parseError, setParseError] = useState('')
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: bulkCreateQualityTests,
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.detail || 'Failed to import quality tests'),
  })

  const handleFile = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setParseError('')

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setParseError(`CSV parse errors: ${results.errors.map(e => e.message).join(', ')}`)
          return
        }
        setParsedRows(results.data)
      },
      error: (err) => setParseError(`Failed to parse CSV: ${err.message}`),
    })
  }

  const handleImport = () => {
    setError('')
    mutation.mutate({
      ranch_id: ranchId,
      tests: parsedRows,
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Bulk Import Quality Tests</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {parseError && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {parseError}
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {parsedRows.length === 0 ? (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:border-emerald-400 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 font-medium">Click to select CSV file</p>
              <p className="text-xs text-gray-400 mt-1">
                Expected columns: test_date, fat_pct, protein_pct, lactose_pct, snf_pct, scc, grade
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFile}
                className="hidden"
              />
            </div>
          ) : (
            <>
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                {parsedRows.length} rows parsed successfully
              </div>

              <div className="overflow-x-auto max-h-64 border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {Object.keys(parsedRows[0]).slice(0, 6).map((key) => (
                        <th key={key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {parsedRows.slice(0, 10).map((row, i) => (
                      <tr key={i}>
                        {Object.values(row).slice(0, 6).map((val, j) => (
                          <td key={j} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {val}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsedRows.length > 10 && (
                <p className="text-xs text-gray-400">Showing first 10 of {parsedRows.length} rows</p>
              )}
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            {parsedRows.length > 0 && (
              <button
                onClick={handleImport}
                disabled={mutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                <Upload className="h-4 w-4" />
                {mutation.isPending ? 'Importing...' : `Import ${parsedRows.length} Tests`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function MilkQualityPage({ ranchId }) {
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [page, setPage] = useState(1)
  const limit = 20

  const { data, isLoading, error } = useQuery({
    queryKey: ['milkQualityTests', ranchId, page],
    queryFn: () => getMilkQualityTests(ranchId, { limit, offset: (page - 1) * limit }),
  })

  const tests = data?.items || data || []
  const totalCount = data?.total || tests.length
  const totalPages = Math.ceil(totalCount / limit)

  const handleSuccess = () => {
    setShowAddModal(false)
    setShowImportModal(false)
    queryClient.invalidateQueries({ queryKey: ['milkQualityTests', ranchId] })
  }

  if (error) {
    return (
      <div className="card p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Failed to load quality tests</h3>
        <p className="text-gray-500 mt-1">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Milk Quality Tests"
        subtitle="Lab results and compositional analysis"
        icon={TestTube}
        breadcrumbs={[
          { label: 'Dairy', href: '/dairy' },
          { label: 'Quality Tests' },
        ]}
        actions={
          <>
            <button
              onClick={() => setShowImportModal(true)}
              className="btn bg-blue-600 text-white hover:bg-blue-700"
            >
              <Upload className="h-4 w-4 mr-2" />
              Bulk Import
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Test
            </button>
          </>
        }
      />

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : tests.length === 0 ? (
          <div className="text-center py-12">
            <TestTube className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No quality tests found</h3>
            <p className="text-gray-500 mt-1">Add your first test to begin tracking milk quality</p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fat %</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Protein %</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">SCC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tests.map((test) => (
                  <tr key={test.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {new Date(test.test_date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {test.animal_name || test.cow_name || 'Bulk Tank'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                      {test.fat_pct != null ? parseFloat(test.fat_pct).toFixed(2) : '--'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                      {test.protein_pct != null ? parseFloat(test.protein_pct).toFixed(2) : '--'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                      {test.somatic_cell_count != null ? Number(test.somatic_cell_count).toLocaleString() : '--'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={clsx(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        gradeColors[test.grade] || 'bg-gray-100 text-gray-800'
                      )}>
                        {gradeLabels[test.grade] || test.grade}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddTestModal
          ranchId={ranchId}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleSuccess}
        />
      )}
      {showImportModal && (
        <BulkImportModal
          ranchId={ranchId}
          onClose={() => setShowImportModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  )
}
